import { useId, type ReactNode } from "react";
import {
  activeFilterCount,
  formatNumber,
  formatSnapshotDate
} from "./engajamentoModel";
import type { EngagementFilters, EngagementOption } from "./types";

type EngagementHeaderProps = {
  eyebrow: string;
  title: string;
  description: string;
  generatedAt?: string;
};

export function EngagementHeader({ eyebrow, title, description, generatedAt }: EngagementHeaderProps) {
  return (
    <header className="engagement-page__header">
      <div>
        <p className="engagement-kicker">{eyebrow}</p>
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
      <span className="engagement-freshness" title={`Última geração: ${formatSnapshotDate(generatedAt)}`}>
        <Icon name="refresh" /> Atualizado em {formatSnapshotDate(generatedAt)}
      </span>
    </header>
  );
}

type FilterBarProps = {
  filters: EngagementFilters;
  options: Record<keyof EngagementFilters, EngagementOption[]>;
  total: number;
  onChange: (key: keyof EngagementFilters, value: string) => void;
  onReset: () => void;
};

const FILTER_LABELS: Record<keyof EngagementFilters, string> = {
  periodo: "Período letivo",
  polo: "Polo",
  curso: "Curso",
  ua: "Unidade acadêmica"
};

export function EngagementFilterBar({ filters, options, total, onChange, onReset }: FilterBarProps) {
  const active = activeFilterCount(filters);
  return (
    <section className="engagement-filters" aria-label="Filtros do painel">
      <div className="engagement-filters__intro">
        <span>Recorte atual</span>
        <strong>{formatNumber(total)} estudantes</strong>
        <small>{active ? `${active} filtro${active === 1 ? "" : "s"} ativo${active === 1 ? "" : "s"}` : "Visão institucional"}</small>
      </div>
      <div className="engagement-filters__fields">
        {(Object.keys(FILTER_LABELS) as Array<keyof EngagementFilters>).map((key) => (
          <label className="engagement-field" key={key}>
            <span>{FILTER_LABELS[key]}</span>
            <select value={filters[key]} onChange={(event) => onChange(key, event.target.value)}>
              <option value="">Todos</option>
              {options[key].map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
        ))}
      </div>
      <button
        type="button"
        className="engagement-button engagement-button--ghost"
        onClick={onReset}
        disabled={!active}
      >
        <Icon name="reset" /> Limpar
      </button>
    </section>
  );
}

type MetricCardProps = {
  label: string;
  value: string;
  detail: string;
  calculation: string;
  tone: "blue" | "green" | "yellow" | "red" | "purple" | "slate";
  icon: IconName;
};

export function EngagementMetricCard({ label, value, detail, calculation, tone, icon }: MetricCardProps) {
  const tooltipId = useId();
  return (
    <article className={`engagement-metric engagement-metric--${tone}`}>
      <div className="engagement-metric__head">
        <span className="engagement-metric__icon"><Icon name={icon} /></span>
        <div className="engagement-tooltip">
          <button type="button" aria-label={`Como é calculado: ${label}`} aria-describedby={tooltipId}>
            <Icon name="info" />
          </button>
          <span id={tooltipId} role="tooltip">{calculation}</span>
        </div>
      </div>
      <span className="engagement-metric__label">{label}</span>
      <strong>{value}</strong>
      <small>{detail}</small>
    </article>
  );
}

export function EngagementPanel({ title, subtitle, children, className = "", help }: {
  title: string;
  subtitle: string;
  children: ReactNode;
  className?: string;
  help?: string;
}) {
  const tooltipId = useId();
  return (
    <section className={`engagement-panel ${className}`.trim()}>
      <header className="engagement-panel__head">
        <div>
          <h2>{title}</h2>
          <p>{subtitle}</p>
        </div>
        {help ? (
          <div className="engagement-tooltip engagement-panel__tooltip">
            <button type="button" aria-label={`Como é calculado: ${title}`} aria-describedby={tooltipId}>
              <Icon name="info" />
            </button>
            <span id={tooltipId} role="tooltip">{help}</span>
          </div>
        ) : null}
      </header>
      <div className="engagement-panel__body">{children}</div>
    </section>
  );
}

export function EngagementLoading() {
  return (
    <div className="engagement-skeleton" aria-label="Carregando painel" role="status">
      <div className="engagement-skeleton__header" />
      <div className="engagement-skeleton__filters" />
      <div className="engagement-skeleton__metrics">
        {Array.from({ length: 6 }, (_, index) => <div key={index} />)}
      </div>
      <div className="engagement-skeleton__panels"><div /><div /><div /></div>
    </div>
  );
}

export function EngagementError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="engagement-error" role="alert">
      <span><Icon name="warning" /></span>
      <div>
        <strong>Não foi possível carregar o painel</strong>
        <p>{message}</p>
      </div>
      <button type="button" className="engagement-button engagement-button--primary" onClick={onRetry}>Tentar novamente</button>
    </div>
  );
}

export function EngagementEmpty({ children = "Nenhum dado disponível para o recorte selecionado." }: { children?: ReactNode }) {
  return <div className="engagement-empty"><Icon name="chart" /><span>{children}</span></div>;
}

export function EngagementPrivacyEmpty() {
  return (
    <div className="engagement-empty engagement-empty--privacy" role="status">
      <Icon name="info" />
      <strong>Recorte protegido</strong>
      <span>O recorte possui menos de cinco estudantes e não é exibido isoladamente. Amplie ou limpe os filtros.</span>
    </div>
  );
}

type IconName = "refresh" | "reset" | "info" | "students" | "check" | "support" | "noAccess" | "cancel" | "sync" | "warning" | "chart";

export function Icon({ name }: { name: IconName }) {
  const paths: Record<IconName, ReactNode> = {
    refresh: <><path d="M20 6v5h-5M4 18v-5h5M18.2 9A7 7 0 0 0 6.3 5.8L4 8M5.8 15A7 7 0 0 0 17.7 18.2L20 16" /></>,
    reset: <><path d="M4 4v6h6M4.7 10A8 8 0 1 1 6 18" /></>,
    info: <><circle cx="12" cy="12" r="9" /><path d="M12 10v6M12 7h.01" /></>,
    students: <><circle cx="9" cy="8" r="3" /><path d="M3.5 20v-2.5A4.5 4.5 0 0 1 8 13h2a4.5 4.5 0 0 1 4.5 4.5V20M15 5.5a3 3 0 0 1 0 5.8M17 14a4.5 4.5 0 0 1 3.5 4.4V20" /></>,
    check: <><circle cx="12" cy="12" r="9" /><path d="m8 12 2.6 2.6L16.5 9" /></>,
    support: <><path d="M4 13v-2a8 8 0 0 1 16 0v2M4 13h3v6H5a1 1 0 0 1-1-1v-5ZM20 13h-3v6h2a1 1 0 0 0 1-1v-5ZM17 19c0 1.1-1.8 2-4 2" /></>,
    noAccess: <><circle cx="12" cy="12" r="9" /><path d="m8.5 8.5 7 7M15.5 8.5l-7 7" /></>,
    cancel: <><path d="M4 7h16M9 7V4h6v3M7 7l1 13h8l1-13M10 11v5M14 11v5" /></>,
    sync: <><path d="M20 7h-5V2M4 17h5v5M18.4 10A7 7 0 0 0 6.2 6L4 9M5.6 14A7 7 0 0 0 17.8 18l2.2-3" /></>,
    warning: <><path d="M12 3 2.7 20h18.6L12 3Z" /><path d="M12 9v5M12 17h.01" /></>,
    chart: <><path d="M4 20V10M10 20V4M16 20v-7M22 20H2" /></>
  };
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{paths[name]}</svg>;
}
